import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';

import { Box, Flex, Heading, List, ListIcon, ListItem, Skeleton, Text } from '@chakra-ui/react';
import { QuestionIcon } from '@udacity/chakra-uds-icons';
import { headerHeight } from '@udacity/udacity-header-v2';

import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { Link } from '~/components/link';
import { Tooltip } from '~/components/tooltip';
import { trackEvent } from '~/features/analytics/track-event';
import { BillingFrequency } from '~/features/checkout/models/checkout';
import {
  SUBSCRIPTION_REFERRER_PROGRAM_KEY_COOKIE,
  SUBSCRIPTION_REFERRER_PROGRAM_TITLE,
  SUBSCRIPTION_REFERRER_URL_COOKIE,
} from '~/features/cookies/constants';
import { CountdownCompactContainer } from '~/features/experiments/ab_plp_redesign_2025/components/countdown-compact';
import { MobileStickyBanner } from '~/features/experiments/ab_plp_redesign_2025/components/mobile-sticky-banner';
import {
  ALL_ACCESS_FEATURES,
  STANDALONE_FEATURES,
} from '~/features/experiments/ab_plp_redesign_2025/components/price-card';
import { PaidPriceCardSlotProps } from '~/features/experiments/ab_plp_redesign_2025/components/program-landing-page';
import { CheckIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { usePricePlans } from '~/features/plans/hooks/use-price-plans';
import { useStandaloneProgramPrice } from '~/features/plans/hooks/use-standalone-program-price';
import { usePromotion } from '~/features/promotions/hooks/use-promotion';

import { shouldSeeAnnualBundlePlan } from '../../../checkout/utils/should-see-annual-plan';

import { CoverWithOverlay } from './cover-with-overlay';
import { DiscountTag } from './discount-tag';
import { getPlanVisibility, getSubscriptionPlans, remapDefaultPlan } from './plan-helpers';
import { PriceRow } from './price-row';

export type ExperimentPriceCardProps = PaidPriceCardSlotProps & {
  imageUrl: string | null;
  programKey: string;
  programSemanticType: string;
  programTitle: string;
};

export function useExperimentPriceCard(props: ExperimentPriceCardProps) {
  const { currency } = useCurrency();
  const isAnnual = shouldSeeAnnualBundlePlan(currency);
  const [selectedPlan, setSelectedPlan] = useState<BillingFrequency>(() =>
    remapDefaultPlan(props.defaultSelectedPlan, isAnnual)
  );
  const { data: prices } = usePricePlans(currency);
  const { data: standalonePlan, isFetched: standalonePlanIsFetched } = useStandaloneProgramPrice({
    currency,
    nodeKey: props.programKey,
  });
  const { data: promotion } = usePromotion();
  const buttonLabel = promotion?.countdownConfig?.buttonLabelOverride ?? 'Enroll Now';
  const subscriptionPlans = useMemo(
    () => getSubscriptionPlans({ isAnnual, plans: prices, semanticType: props.programSemanticType }),
    [isAnnual, prices, props.programSemanticType]
  );
  const visibility = getPlanVisibility({
    hasStandalonePlan: Boolean(standalonePlan),
    semanticType: props.programSemanticType,
    standalonePlanIsFetched,
  });

  useEffect(() => {
    setSelectedPlan(remapDefaultPlan(props.defaultSelectedPlan, isAnnual));
  }, [isAnnual, props.defaultSelectedPlan]);

  function selectPlan(plan: BillingFrequency) {
    trackEvent({
      additionalAttributes: { product_ids: [props.programKey] },
      label: `${plan}_pricecard_clicked`,
      name: 'Product Page CTA Clicked',
      type: 'click',
    });
    setSelectedPlan(plan);
  }

  return {
    buttonLabel,
    isAnnual,
    promotion,
    selectedPlan,
    setSelectedPlan: selectPlan,
    standalonePlan,
    subscriptionPlans,
    visibility,
  };
}

export function ExperimentCardShell({
  buttonSlot,
  collapseCoverWhenSticky = true,
  children,
  expandedCoverHeight,
  imageUrl,
  isMobile,
  promotion,
  renderCover = true,
  showCoverPromotionOverlay = true,
}: {
  buttonSlot: ReactNode;
  collapseCoverWhenSticky?: boolean;
  children: ReactNode;
  expandedCoverHeight?: number;
  imageUrl: string | null;
  isMobile: boolean;
  promotion: ReturnType<typeof useExperimentPriceCard>['promotion'];
  renderCover?: boolean;
  showCoverPromotionOverlay?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showMobileBanner, setShowMobileBanner] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!isMobile || !node) return;
    const observer = new IntersectionObserver(([entry]) => {
      setShowMobileBanner(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile]);

  return (
    <>
      <Flex
        ref={containerRef}
        data-testid={isMobile ? 'mobile-experiment-price-card' : 'desktop-experiment-price-card'}
        display={isMobile ? { base: 'flex', lg: 'none' } : 'flex'}
        flexDirection='column'
        isolation='isolate'
        margin={isMobile ? '0 auto 24px' : '0 0 0 auto'}
        maxWidth={isMobile ? '100%' : '356px'}
        position={isMobile ? 'relative' : 'sticky'}
        scrollMarginTop={`calc(${headerHeight} + 16px)`}
        top={isMobile ? undefined : `calc(${headerHeight} + 16px)`}
        width='100%'
      >
        {renderCover && (
          <CoverWithOverlay
            collapseWhenSticky={collapseCoverWhenSticky}
            expandedHeight={expandedCoverHeight}
            imageUrl={imageUrl}
            isMobile={isMobile}
            promotion={promotion}
            showPromotionOverlay={showCoverPromotionOverlay}
          />
        )}
        {children}
      </Flex>
      {isMobile && (
        <MobileStickyBanner
          isVisible={showMobileBanner}
          buttonSlot={
            <Box
              width='100%'
              sx={{
                'a, button': {
                  borderRadius: '4px',
                },
              }}
            >
              {buttonSlot}
            </Box>
          }
        />
      )}
    </>
  );
}

export function SubscriptionPanel({
  plans,
  selectedPlan,
  onSelect,
}: {
  plans: ReturnType<typeof useExperimentPriceCard>['subscriptionPlans'];
  selectedPlan: BillingFrequency;
  onSelect: (plan: BillingFrequency) => void;
}) {
  return (
    <>
      <Flex flexDirection='column' gap={2}>
        {plans.map((plan) => (
          <PriceRow
            key={plan.value}
            isSelected={selectedPlan === plan.value}
            plan={plan}
            onSelect={() => onSelect(plan.value)}
          />
        ))}
      </Flex>
      {plans.length > 0 && <FeatureList features={ALL_ACCESS_FEATURES} />}
    </>
  );
}

export function FeatureList({ features }: { features: string[] }) {
  return (
    <List display='flex' flexDirection='column' gap={2}>
      {features.map((feature) => (
        <ListItem key={feature} display='flex'>
          <ListIcon alignSelf='flex-start' as={CheckIcon} color='#8A8D94' height='24px' width='24px' />
          <HeadingWithEmphasis
            emphasisProps={{ fontWeight: '600' }}
            text={feature}
            headingProps={{
              as: 'span',
              fontFamily: 'body',
              fontSize: '15px',
              fontWeight: '400',
              lineHeight: '18px',
            }}
          />
        </ListItem>
      ))}
    </List>
  );
}

export function StandalonePanel({ plan }: { plan: ReturnType<typeof useExperimentPriceCard>['standalonePlan'] }) {
  return (
    <>
      <Text color='#5A5C62' fontSize='14px' fontWeight='400' lineHeight='20px'>
        Access to only this program
      </Text>
      <Flex
        alignItems='center'
        bg='#F4F8FF'
        border='1px solid #175CFF'
        borderRadius='8px'
        height='68px'
        justifyContent='space-between'
        paddingX={3}
      >
        <Flex alignItems='center' gap={3}>
          <Flex
            alignItems='center'
            border='1.2px solid #175CFF'
            borderRadius='full'
            height='18px'
            justifyContent='center'
            width='18px'
          >
            <Box bg='#175CFF' borderRadius='full' height='8px' width='8px' />
          </Flex>
          <Text color='#0B0B0B' fontSize='16px' fontWeight='500'>
            One-time payment
          </Text>
        </Flex>
        {plan?.displayPrice ? (
          <Box textAlign='right'>
            {plan.strikeThroughDisplayPrice && (
              <Flex alignItems='center' gap={1} justifyContent='flex-end'>
                <Text as='del' color='#5A5C62' fontSize='12px' fontWeight='600'>
                  {plan.strikeThroughDisplayPrice}
                </Text>
                {plan.couponApplied && typeof plan.couponPercentOff === 'number' && (
                  <DiscountTag value={plan.couponPercentOff} />
                )}
              </Flex>
            )}
            <Heading as='p' fontFamily='body' fontSize='26px' fontWeight='600' lineHeight='28px' whiteSpace='nowrap'>
              {plan.displayPrice}
            </Heading>
          </Box>
        ) : (
          <Skeleton height='28px' width='92px' />
        )}
      </Flex>
      <FeatureList features={STANDALONE_FEATURES} />
    </>
  );
}

export function PriceCardCountdown({
  renderButton,
  selectedPlan,
  showSticker = false,
}: Pick<PaidPriceCardSlotProps, 'renderButton'> & { selectedPlan: BillingFrequency; showSticker?: boolean }) {
  return (
    <Box
      sx={{
        'a, button': {
          borderRadius: '4px',
          fontSize: '17px',
          fontWeight: '600',
          height: '50px',
          lineHeight: '26px',
        },
      }}
    >
      <CountdownCompactContainer renderButton={renderButton} selectedPlan={selectedPlan} showSticker={showSticker} />
    </Box>
  );
}

export function StandalonePurchaseLink({
  displayPrice,
  programKey,
  programTitle,
  showTooltip = false,
  strikeThroughDisplayPrice,
}: {
  displayPrice?: string;
  programKey: string;
  programTitle: string;
  showTooltip?: boolean;
  strikeThroughDisplayPrice?: string | null;
}) {
  const href = `/checkout/individual/purchase/credit-card?nodeKey=${encodeURIComponent(programKey)}`;
  return (
    <Flex alignItems='center' gap={2} justifyContent='center' width='100%'>
      <Link
        color='supportGray.90 !important'
        fontSize='15px'
        fontWeight='600'
        href={href}
        lineHeight='18px'
        sx={{ textUnderlineOffset: '3px' }}
        textAlign='center'
        textDecoration='underline'
        onClick={() => {
          Cookies.set(SUBSCRIPTION_REFERRER_URL_COOKIE, window.location.pathname, { expires: 1 });
          Cookies.set(SUBSCRIPTION_REFERRER_PROGRAM_KEY_COOKIE, programKey, { expires: 1 });
          Cookies.set(SUBSCRIPTION_REFERRER_PROGRAM_TITLE, programTitle, { expires: 1 });
          trackEvent({
            additionalAttributes: { product_ids: [programKey] },
            label: 'onetime_purchase_enroll_now',
            name: 'Product Page CTA Clicked',
            type: 'click',
          });
        }}
      >
        Buy just this program
        {displayPrice && (
          <>
            {' for '}
            {strikeThroughDisplayPrice && (
              <>
                <Box as='del' color='#A2A3AE' fontWeight='600'>
                  {strikeThroughDisplayPrice}
                </Box>{' '}
              </>
            )}
            {displayPrice}
          </>
        )}
      </Link>
      {showTooltip && (
        <Tooltip
          aria-label='Learn more about the standalone purchase option'
          arrowProps={{ bg: '#3A3A3A' }}
          placement='top'
          contentProps={{
            bg: '#3A3A3A',
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(11, 11, 11, 0.1), 0 1px 2px rgba(11, 11, 11, 0.06)',
            color: 'white',
            maxWidth: '219px',
            padding: '4px 8px',
            width: '219px',
          }}
          label={
            <Text fontFamily='body' fontSize='14px' fontWeight='600' lineHeight='22px' textAlign='center'>
              One payment. Flexibility to study the program in your own time. Career coaching and access to the full
              catalog come with the subscription.
            </Text>
          }
          hasArrow
          onOpen={() => {
            trackEvent({
              additionalAttributes: { product_ids: [programKey] },
              label: 'onetime_purchase_tooltip_clicked',
              name: 'Product Page CTA Clicked',
              type: 'click',
            });
          }}
        >
          <QuestionIcon color='#9C9FA8' height='16px' width='16px' />
        </Tooltip>
      )}
    </Flex>
  );
}

export function BundleDisclaimer({ bundleDisplayPrice, isAnnual }: { bundleDisplayPrice?: string; isAnnual: boolean }) {
  return (
    <Flex color='#A2A3AE' flexDirection='column' gap='4px' minHeight='68px'>
      <Text fontSize='10px' fontWeight='400' lineHeight='16px'>
        <Box as='sup'>1</Box> Discount applies to the first {isAnnual ? 'year' : '4 months'} of membership, after which
        plans are converted to month-to-month.
      </Text>
      <Text fontSize='10px' fontWeight='400' lineHeight='16px'>
        <Box as='sup'>2</Box> Total price of {bundleDisplayPrice ?? 'the bundle price'} for{' '}
        {isAnnual
          ? 'an annual subscription.'
          : 'a 4-month bundle, based on the average time to complete a Nanodegree program.'}
      </Text>
    </Flex>
  );
}
